import React, {useEffect, useState} from 'react';
import {useParams} from "react-router-dom";
import {
    adminAddProductSpecificQuestion, adminBatchUpdateProductSpecificQuestions,
    adminDeleteProductSpecificQuestion, adminGetFilteredProductSpecificQuestions,
    adminGetProductSpecificQuestions,
    adminUpdateProductSpecificQuestion,
    getProducts
} from '../../../services/api/admin';
import {Edit, TrashBin} from "flowbite-react-icons/outline";
import Modal from "react-modal";
import ConfirmationModal from "../../../components/Modal/ConfirmationModal";
import LoadSpinner from "../../../components/Common/LoadSpinner";
import ModalAddEditProductSpecificQuestion from "../../../components/Modal/ModalAddEditProductSpecificQuestion";
import DraggableTable from "../../Block/Table/DraggableTable";
import DropdownSelect from "../../Formik/DropdownSelect";
import {useFormik} from "formik";
import ListPagination from "../../Block/Pagination/ListPagination";
import PaginatedTable from "../../Block/Table/PaginatedTable";

const ProductSpecificQuestionsList = () => {
    const headers = [
        {data: 'Product', classes: 'sl-border-right sl-font-bold'},
        {data: 'Question', classes: 'sl-font-bold'},
        {data: 'Question short', classes: 'sl-font-bold'},
        {data: 'Hover text', classes: 'sl-font-bold'},
        {data: 'Sort order', classes: 'sl-font-bold'},
        {data: 'Appearance', classes: 'sl-font-bold'},
    ];

    const [psqData, setPsqData] = useState([]);
    const [reorderedData, setReorderedData] = useState([]);
    const [psqDataFetched, setPsqDataFetched] = useState(false);
    const [allProducts, setAllProducts] = useState([]);

    const [deleteRequestOpen, setDeleteRequestOpen] = useState(false);
    const [editRequestOpen, setEditRequestOpen] = useState(false);
    const [addRequestOpen, setAddRequestOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [addError, setAddError] = useState('');

    const {per_page} = useParams();
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({
        total: 0,
        count: 0,
        per_page: parseInt(per_page, 10) || 10,
        current_page: parseInt(page, 10) || 1,
        total_pages: 1
    });

    const [activePsqFilterOptions, setActivePsqFilterOptions] = useState({
        page: pagination.current_page,
        per_page: pagination.per_page,
    });

    const formik = useFormik({
        initialValues: { product: null },
        onSubmit: () => { onClickReset(false); }
    });

    useEffect(() => {
        const fetchProducts = async () => {
            const products = await getProducts();
            if (products) {
                setAllProducts(products);
            }
        };
        fetchProducts();
    }, []);

    useEffect(() => {
        if (!page && !per_page) {
            onClickReset(true);
            return
        }

        setActivePsqFilterOptions({
            ...activePsqFilterOptions,
            page: page || 1,
            per_page: per_page || 10
        })
        setPsqData([]);
        setPsqDataFetched(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, per_page]);

    useEffect(() => {
        if (!psqDataFetched && psqData.length === 0) {
            fetchPsq().then(r => console.log('Product specific questions fetched'));
        }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [psqDataFetched, psqData]);

    const fetchPsq = async () => {
        if (!psqData.length && !psqDataFetched)
            try {
                let data = null;
                let meta = null;

                if (formik.values.product !== null) {
                    data = await adminGetFilteredProductSpecificQuestions(formik.values.product.id);
                } else {
                    const response = await adminGetProductSpecificQuestions(activePsqFilterOptions);
                    data = response.data;
                    meta = response.meta;

                    setPagination(meta.pagination);
                    setActivePsqFilterOptions({
                        ...activePsqFilterOptions,
                        page: meta.pagination.current_page,
                        per_page: meta.pagination.per_page
                    });
                }

                const transformedData = data.map(item => {
                    const tempData = {};

                    if (item.product_name !== undefined) {
                        tempData.product_name = {
                            data: item.product_name,
                            classes: "sl-min-w-10 sl-max-w-34"
                        };
                    }
                    if (item.question !== undefined) {
                        tempData.question = {
                            data: item.question,
                            classes: "sl-min-w-10 sl-w-55 sl-text-left",
                            id: item.id,
                        };
                    }
                    if (item.question_short !== undefined) {
                        tempData.question_short = {
                            data: item.question_short,
                            classes: "sl-min-w-10 sl-w-55 sl-text-left"
                        };
                    }
                    if (item.hover_text !== undefined) {
                        tempData.hover_text = {
                            data: item.hover_text,
                            classes: "sl-min-w-10 sl-max-w-64 sl-text-left"
                        };
                    }
                    if (item.sort_order !== undefined) {
                        tempData.sort_order = {
                            data: item.sort_order,
                            classes: "sl-min-w-10"
                        };
                    }
                    if (item.appearance !== undefined) {
                        tempData.appearance = {
                            data: item.appearance ? 'Visible' : 'Hidden',
                            classes: "sl-min-w-10"
                        };
                    }

                    if (formik.values.product !== null) {
                        tempData.edit = {
                            data: <Edit size={20} color={"#344054"} className={"sl-m-auto"} />,
                            onClick: () => handleToggleEdit(item),
                            classes: "sl-action-edit sl-cursor-pointer"
                        };

                        tempData.delete = {
                            data: <TrashBin size={20} color={"#344054"} className={"sl-m-auto"} />,
                            onClick: () => handleToggleDelete(item),
                            classes: "sl-action-delete sl-border-right sl-cursor-pointer"
                        };
                    }

                    return tempData;
                });
                setPsqDataFetched(true);
                setPsqData(transformedData);
            } catch (e) {
                console.error('Error fetching product specific questions:', e);
            }
    }

    const handleToggleAdd = () => {
        setAddError('');
        setAddRequestOpen(!addRequestOpen);
    };

    const handleAdd = async ({productId, question, questionShort, hoverText, appearance}) => {
        try {
            const data = await adminAddProductSpecificQuestion(productId, question, questionShort, hoverText, appearance);

            if (data.data.message !== 'Successful') {
                setAddError(data.data.message);
            } else {
                handleToggleAdd();
                await formik.setFieldValue('product', allProducts.find(item => item.id === productId));
                onClickReset(false);
                scrollToItem();
                setAddError('')
            }
        } catch (e) {
            console.error('Error adding product specific question:', e);
        }
    }

    const scrollToItem = () => {
        setTimeout(() => {
            setTimeout(() => {
                const table = document.querySelector('table');
                const tableRect = table.getBoundingClientRect();
                const tableBottom = tableRect.bottom;
                const viewportHeight = window.innerHeight;

                if (tableBottom > viewportHeight) {
                    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                }
            }, 50);
        }, 500);
    }

    const handleToggleEdit = (item) => {
        setEditRequestOpen(!editRequestOpen);
        setSelectedRequest(item);
    };

    const handleEdit = async ({id, question, questionShort, hoverText, appearance}) => {
        try {
            await adminUpdateProductSpecificQuestion(id, question, questionShort, hoverText, appearance);
            handleToggleEdit(selectedRequest);
            onClickReset(false);
        } catch (e) {
            console.error('Error editing product specific question:', e);
        }
    }

    const handleToggleDelete = (item) => {
        setDeleteRequestOpen(!deleteRequestOpen);
        setSelectedRequest(item);
    };

    const handleDelete = async (item) => {
        try {
            await adminDeleteProductSpecificQuestion(item.id);

            const updatedData = psqData.filter((row) => row.question.id !== item.id);
            const updatedDataWithNewSortOrder = updatedData.map((row, index) => ({
                ...row,
                sort_order: { ...row.sort_order, data: index + 1 },
            }));
            setPsqData(updatedDataWithNewSortOrder);

            if (updatedDataWithNewSortOrder.length > 0) {
                const reorderedPayload = updatedDataWithNewSortOrder.map((row) => ({
                    id: row.question.id,
                    sort_order: row.sort_order.data,
                }));
                await adminBatchUpdateProductSpecificQuestions(reorderedPayload);
            }

            setDeleteRequestOpen(false);
            onClickReset(false);
        } catch (e) {
            console.error('Error deleting product specific question:', e);
        }
    }

    const handleSaveOrder = async () => {
        const reorderedPayload = reorderedData.length > 0
            ? reorderedData.map((row, index) => ({
                id: row.question.id,
                sort_order: index + 1
            }))
            : psqData.map((row, index) => ({
                id: row.question.id,
                sort_order: index + 1
            }));

        if(reorderedPayload.length === 0) return;

        try {
            await adminBatchUpdateProductSpecificQuestions(reorderedPayload);
            onClickReset(false);
        } catch (error) {
            console.error('Error updating reorder:', error);
        }
    };

    const onClickReset = (resetProduct = true) => {
        setActivePsqFilterOptions({
            page: 1,
            per_page: 10,
        })
        setPage(1);

        if (resetProduct)
            formik.setFieldValue('product', null);

        setPsqData([]);
        setPsqDataFetched(false);
    }

    return (
        <div>
            <main className="sl-main sl-mt-8 sl-mb-21">
                <div className="sl-container sl-m-auto sl-my-8 sl-flex sl-flex-col sl-gap-3">
                    <div className={"sl-flex sl-flex-row sl-justify-space-between"}>
                        <div className={"sl-flex sl-flex-row sl-gap-2 sl-relative"}>
                            <div>
                                <DropdownSelect
                                    formik={formik}
                                    name={'product'}
                                    selectedItem={formik.values.product}
                                    fieldDefaultText={'Select product to manage questions'}
                                    options={allProducts}
                                    onChange={(selected) => {
                                        formik.setFieldValue('product', selected);
                                        setPsqDataFetched(false);
                                        setPsqData([]);
                                    }}
                                    maxLabelLength={30}
                                    elementClasses={'sl-min-w-86 sl-max-w-86 sl-text-left sl-overflow-y-hidden sl-absolute sl-z-10'}
                                >
                                </DropdownSelect>
                            </div>

                            <div className={"sl-flex sl-gap-2 sl-absolute sl-left-300 "}>
                                <span onClick={onClickReset} className={'sl-btn sl-btn-sm sl-btn-tertiary sl-py-10'}>Reset</span>
                            </div>
                        </div>

                        {formik.values.product !== null && (
                            <div className={`sl-flex sl-gap-2`}>
                                <span onClick={handleSaveOrder} className={'sl-btn sl-btn-sm sl-btn-tertiary sl-py-10'}>Save order</span>
                                <span onClick={handleToggleAdd} className={'sl-btn sl-btn-sm sl-btn-primary sl-py-10'}>Add new</span>
                            </div>
                        )}
                    </div>

                    {deleteRequestOpen && <ConfirmationModal
                        title={`Delete question`}
                        message={'Are you sure you want to delete question "' + selectedRequest.question +
                            '" for product ' + selectedRequest.product_name + '?'}
                        btnConfirm={'Delete'}
                        handleConfirm={async () => handleDelete(selectedRequest)}
                        handleClose={() => {
                            setDeleteRequestOpen(false);
                            setSelectedRequest(null);
                        }}
                    />}

                    {selectedRequest && <Modal
                        isOpen={editRequestOpen}
                        onRequestClose={handleToggleEdit}
                        className="sl-modal-content sl-max-h-185"
                        overlayClassName="sl-modal sl-block sl-rounded-md sl-bg-gray-900-opacity-50">
                        <ModalAddEditProductSpecificQuestion
                            item={selectedRequest}
                            handleAction={handleEdit}
                            onHandleToggle={handleToggleEdit}
                            products={allProducts}
                            title={"Edit question"}
                            message={'Update the question for product ' + selectedRequest.product_name}
                        />
                    </Modal>}

                    {addRequestOpen && <Modal
                        isOpen={addRequestOpen}
                        onRequestClose={handleToggleAdd}
                        className="sl-modal-content sl-max-h-185"
                        overlayClassName="sl-modal sl-block sl-rounded-md sl-bg-gray-900-opacity-50">
                        <ModalAddEditProductSpecificQuestion
                            products={allProducts}
                            handleAction={handleAdd}
                            onHandleToggle={handleToggleAdd}
                            title={"Add question"}
                            message={'Provide the following details to add a new product specific question:'}
                            errorMessage={addError}
                        />
                    </Modal>}

                    {psqDataFetched ? (
                        <div className="sl-table-container">
                            {formik.values.product !== null ? (
                                <DraggableTable
                                    headers={headers}
                                    data={psqData}
                                    onReorder={(updatedData) => setReorderedData(updatedData)}
                                />
                            ) : (
                                <div className={'sl-mt-12'}>
                                    <PaginatedTable
                                        headers={headers}
                                        data={psqData}
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="sl-self-center sl-mt-12">
                            <LoadSpinner />
                        </div>
                    )}

                    {formik.values.product === null && pagination?.total_pages > 1 && psqDataFetched && (
                        <ListPagination
                            paginationData={pagination}
                            onPageChange={(newPage) => setPage(newPage)}
                        />
                    )}
                </div>
            </main>
        </div>
    );
};
export default ProductSpecificQuestionsList;